import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { appAPI, shareAPI } from '../services/api';

const APKsListing = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [appTokens, setAppTokens] = useState({});

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await appAPI.getAllApps();
      const approvedApps = response.data.filter(app => app.status === 'approved');
      setApps(approvedApps);
      
      // Create share links for each app
      const tokens = {};
      for (const app of approvedApps) {
        try {
          const shareResponse = await shareAPI.createShareLink(
            app.id,
            `Download ${app.app_name}`,
            app.description,
            null
          );
          if (shareResponse.success) {
            tokens[app.id] = shareResponse.data.token;
          }
        } catch (error) {
          console.error(`Error creating share link for app ${app.id}:`, error);
        }
      }
      setAppTokens(tokens);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching apps:', error);
      setLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    
    // Get base URL without /api suffix
    const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'https://apibackend.sisateck.com';
    
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  const categories = ['all', ...new Set(apps.map(app => app.category))];
  
  const filteredApps = apps.filter(app => {
    // Category filter
    const matchesCategory = filter === 'all' || app.category === filter;
    
    // Search filter
    const matchesSearch = searchQuery === '' || 
      app.app_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.package_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen pt-16">
      {/* Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0a0a0a] to-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-black mb-4">
              Browse <span className="text-gradient">APKs</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
              Download our collection of powerful Android applications
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search apps by name, description, or package..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl px-6 py-4 pl-12 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                />
                <svg 
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-6 py-2 rounded-xl font-semibold transition-all ${
                  filter === category
                    ? 'bg-green-500 text-black'
                    : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                }`}
              >
                {category === 'all' ? 'All Apps' : category}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">
                {searchQuery || filter !== 'all' ? `${filteredApps.length} / ${apps.length}` : apps.length}
              </div>
              <div className="text-gray-500">{searchQuery || filter !== 'all' ? 'Showing / Total' : 'Total Apps'}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">
                {apps.reduce((sum, app) => sum + (app.total_downloads || 0), 0)}+
              </div>
              <div className="text-gray-500">Downloads</div>
            </div>
          </div>
        </div>
      </section>

      {/* Apps Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500"></div>
              <p className="text-gray-500 mt-4 text-lg">Loading apps...</p>
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-xl">No apps found in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredApps.map((app) => (
                <Link
                  key={app.id}
                  to={appTokens[app.id] ? `/s/${appTokens[app.id]}` : `/download`}
                  className="group bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#2a2a2a] hover:border-green-500/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-green-500/20"
                >
                  {/* App Icon */}
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={getImageUrl(app.icon_url)}
                      alt={app.app_name}
                      className="w-16 h-16 rounded-xl object-cover border border-[#2a2a2a] group-hover:border-green-500/30 transition-colors"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg group-hover:text-green-400 transition-colors truncate">
                        {app.app_name}
                      </h3>
                      <p className="text-sm text-gray-500">{app.category}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-400 line-clamp-2 mb-4 min-h-[40px]">
                    {app.description}
                  </p>

                  {/* Stats */}
                  <div className="pt-4 border-t border-[#2a2a2a] flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 text-gray-500">
                      <span className="flex items-center gap-1">
                        ⭐ {app.rating || '4.5'}
                      </span>
                      <span className="flex items-center gap-1">
                        📦 {app.size_mb} MB
                      </span>
                    </div>
                    <span className="text-green-500 text-sm font-semibold group-hover:translate-x-1 transition-transform">
                      →
                    </span>
                  </div>

                  {/* Version Badge */}
                  <div className="mt-3 flex items-center gap-2">
                    <span className="inline-block bg-green-500/10 text-green-400 text-xs font-semibold px-2 py-1 rounded">
                      {app.version}
                    </span>
                    <span className="text-xs text-gray-600">
                      ⬇️ {app.total_downloads || 0}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default APKsListing;
